import moment from 'moment'
import { orderBy } from 'lodash'
import { NEXT_RECURRING_APPOINTMENT_SLOT } from '@src/graphql/nextRecurringAppointmentSlot.ts'
import Model from '@models/model'
import { ALL_NOTES } from '@src/screens/NutritionistHub/graphql/allNotes.ts'
import { memberNotesUpdatedAtTimestampSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import Storage from '@src/utils/storage'
import {
  AppointmentEhrSortField,
  BillingAllowance,
  BillingAllowanceKind,
  CoachAssignmentDynamicFilterField,
  CoachAssignmentSupportType,
  DynamicFilterItemOperation,
  DynamicFilterOperator,
  EhrAppointmentCategory,
  FilterAppointmentsKind,
  NoteCollection,
  EhrSlot,
  SortDirection,
} from '@src/types'
import { CREATE_CHART_NOTE } from '@screens/NutritionistHub/graphql/ehr/createEhrChartNote.ts'
import { RATE_COACH_APPOINTMENT } from '@src/graphql/rateCoachAppointment.ts'
import { SEND_VIDEO_CALL_REFERRAL } from '@src/graphql/sendVideoCallReferral.ts'
import { SendVideoCallReferralMutation } from '@src/graphql/sendVideoCallReferral.generated.ts'
import { FETCH_POTENTIALLY_RECURRING_SLOTS } from '@src/graphql/fetchPotentiallyRecurringSlots.ts'
import { FETCH_ALL_COACH_PROFILES } from '@src/graphql/fetchAllCoachProfiles.ts'
import { FETCH_COACH_PROFILE } from '@src/graphql/fetchCoachProfile.ts'
import { FETCH_COACH_SWITCHING_SUMMARY } from '@src/graphql/fetchCoachSwitchingSummary.ts'
import { SUBMIT_INSURANCE_INFORMATION } from '@src/graphql/submitInsuranceInformation.ts'
import { FETCH_APPOINTMENT_RECURRENCE } from '../graphql/ehr/fetchAppointmentRecurrence'
import { UPSERT_APPOINTMENT_RECURRENCE } from '../graphql/ehr/upsertEhrAppointmentRecurrence'
import { DELETE_APPOINTMENT_RECURRENCE } from '../graphql/ehr/deleteEhrAppointmentRecurrence'
import { FETCH_INSURANCE_POLICY } from '../graphql/fetchInsurancePolicy'
import { FETCH_COACH_ASSIGNMENTS } from '../graphql/fetchCoachAssignments'
import { FETCH_INSURANCE_CARD_SUBMISSION } from '../graphql/fetchInsuranceCardSubmission.ts'
import SUBMIT_INSURANCE_CARD from '../graphql/submitInsuranceCard.ts'
import { FETCH_APPOINTMENT_INFO } from '../graphql/fetchAppointmentInfo.ts'
import { FETCH_EHR_EARLIER_AVAILABLE_SLOTS } from '../graphql/fetchEhrEarlierAvailableSlots.ts'
import { FETCH_EHR_AVAILABLE_DATES } from '../graphql/fetchEhrAvailableDates.ts'
import { FETCH_EHR_AVAILABLE_SLOTS } from '../graphql/fetchEhrAvailableSlots.ts'
import { BILLING_ALLOWANCES } from '../graphql/billingAllowances.ts'
import { EHR_APPOINTMENT } from '../graphql/ehrAppointment.ts'
import { EHR_APPOINTMENTS } from '../graphql/ehrAppointments.ts'
import { RESCHEDULE_APPOINTMENT } from '../graphql/rescheduleAppointment.ts'
import { CANCEL_APPOINTMENT } from '../graphql/cancelAppointment.ts'
import { FETCH_EHR_SUGGESTED_AVAILABILITY } from '../graphql/fetchEhrSuggestedAvailability.ts'
import CLAIM_FREE_APPOINTMENT from '../graphql/claimFreeAppointment.ts'
import { BOOK_APPOINTMENT } from '../graphql/bookAppointment.ts'
import { FETCH_EHR_ALL_AVAILABILITIES } from '../graphql/fetchEhrAllAvailabilities.ts'
import { FETCH_EHR_AVAILABLE_DATES_WITH_SLOTS } from '../graphql/fetchEhrAvailableDatesWithSlots'
import { UPDATE_APPOINTMENT_KIND } from '../graphql/updateAppointmentKind.ts'
import { EHR_APPOINTMENT_SCHEDULING_INFO } from '../graphql/ehrAppointmentSchedulingInfo.ts'
import APPLY_FREE_APPOINTMENT from '../graphql/applyFreeAppointment.ts'
import reducers from './nutritionistHub.reducers'

export default class NutritionistHub {
  namespace = 'nutritionistHub'

  state = {
    ...Model.defaultState,
    latestUserEhrAppointment: null,
    ehrAppointmentSchedulingInfo: null,
    insurancePolicy: null,
    coachAssignment: null,
    allMemberNotes: [],
    memberNotesUpdatedAt: false,
    allEhrAppointments: [],
    upcomingEhrAppointments: [],
    pastEhrAppointments: [],
    pendingEhrAppointments: [],
    ehrSuggestedAvailability: null,
    ehrEarlierAvailableSlotsByAppointmentId: {},
    coachingCallAllowance: null,
    coachProfiles: [],
    coachProfile: null,
  }

  effects = {
    fetchAppointmentInfo: Model.buildEffect({
      name: `${this.namespace}/fetchAppointmentInfo`,
      query: FETCH_APPOINTMENT_INFO,
      variables: { category: EhrAppointmentCategory.Coaching },
      caching: false,
      dataPath: 'latestUserEhrAppointment',
      warnings: false, // disable warnings as response may be empty
      reducers: [{ name: 'updateLastUserEhrAppointment' }],
    }),
    fetchAppointmentSchedulingInfo: Model.buildEffect({
      name: `${this.namespace}/fetchAppointmentSchedulingInfo`,
      query: EHR_APPOINTMENT_SCHEDULING_INFO,
      caching: false,
      dataPath: 'ehrAppointmentSchedulingInfo',
      reducers: [{ name: 'updateEhrSchedulingConfig' }],
    }),
    fetchEhrAvailableDays: Model.buildEffect({
      name: `${this.namespace}/fetchEhrAvailableDays`,
      query: FETCH_EHR_AVAILABLE_DATES,
      caching: false,
      dataPath: 'ehrAvailableDates',
    }),
    fetchEhrAvailableSlots: Model.buildEffect({
      name: `${this.namespace}/fetchEhrAvailableSlots`,
      query: FETCH_EHR_AVAILABLE_SLOTS,
      caching: false,
      dataPath: 'ehrAvailableDateSlots',
    }),
    fetchEhrEarlierAvailableSlots: Model.buildEffect({
      name: `${this.namespace}/fetchEhrEarlierAvailableSlots`,
      query: FETCH_EHR_EARLIER_AVAILABLE_SLOTS,
      caching: false,
      dataPath: 'ehrEarlierAvailableSlots',
      warnings: false, // disable warnings as response may be empty
      reducers: [
        {
          name: 'updateEhrEarlierAvailableSlots',
          payload: (
            response: { slots: EhrSlot[] },
            { variables }: { variables: { appointmentId: string } },
          ) => {
            return { appointmentId: variables.appointmentId, slots: response.slots }
          },
        },
      ],
    }),
    fetchEhrSuggestedAvailability: Model.buildEffect({
      name: `${this.namespace}/fetchEhrSuggestedAvailability`,
      query: FETCH_EHR_SUGGESTED_AVAILABILITY,
      caching: false,
      dataPath: 'ehrSuggestedAvailability',
      reducers: [{ name: 'updateEhrSuggestedAvailability' }],
    }),
    fetchEhrAllAvailabilities: Model.buildEffect({
      name: `${this.namespace}/fetchEhrAllAvailabilities`,
      query: FETCH_EHR_ALL_AVAILABILITIES,
      caching: false,
      dataPath: 'ehrAllAvailabilities',
    }),
    fetchPotentiallyRecurringSlots: Model.buildEffect({
      name: `${this.namespace}/fetchPotentiallyRecurringSlots`,
      query: FETCH_POTENTIALLY_RECURRING_SLOTS,
      caching: false,
      dataPath: 'ehrPotentiallyRecurringSlots',
    }),
    bookAppointment: Model.buildEffect({
      name: `${this.namespace}/bookAppointment`,
      query: BOOK_APPOINTMENT,
      caching: false,
      dataPath: 'ehrBookAppointment',
      reducers: [
        { name: 'updateLastUserEhrAppointment' },
        { name: 'fetchAppointmentInfo' },
        { name: 'fetchCoachingCallAllowance' },
        { name: 'uiFlags/fetch' },
      ],
    }),
    cancelAppointment: Model.buildEffect({
      name: `${this.namespace}/cancelAppointment`,
      query: CANCEL_APPOINTMENT,
      caching: false,
      dataPath: 'cancelEhrAppointment',
      reducers: [{ name: 'fetchCoachingCallAllowance' }],
    }),
    rescheduleAppointment: Model.buildEffect({
      name: `${this.namespace}/rescheduleAppointment`,
      query: RESCHEDULE_APPOINTMENT,
      caching: false,
      dataPath: 'rescheduleEhrAppointment',
    }),
    updateAppointmentKind: Model.buildEffect({
      name: `${this.namespace}/updateAppointmentKind`,
      query: UPDATE_APPOINTMENT_KIND,
      dataPath: 'updateEhrAppointmentKind',
      reducers: [{ name: 'fetchAppointmentInfo' }, { name: 'fetchUpcomingEhrAppointments' }],
    }),
    fetchInsurancePolicy: Model.buildEffect({
      name: `${this.namespace}/fetchInsurancePolicy`,
      query: FETCH_INSURANCE_POLICY,
      caching: false,
      warnings: false, // disable warnings as response is empty
      dataPath: 'insurancePolicy',
      reducers: [{ name: 'updateInsurancePolicy' }],
    }),
    submitInsuranceInformation: Model.buildEffect({
      name: `${this.namespace}/submitInsuranceInformation`,
      query: SUBMIT_INSURANCE_INFORMATION,
      caching: false,
      dataPath: 'submitInsuranceInformation',
    }),
    fetchCoachAssignments: Model.buildEffect({
      name: `${this.namespace}/fetchCoachAssignments`,
      query: FETCH_COACH_ASSIGNMENTS,
      variables: {
        dynamicFilters: {
          items: [
            {
              field: CoachAssignmentDynamicFilterField.SupportType,
              operation: DynamicFilterItemOperation.In,
              value: [
                CoachAssignmentSupportType.VideoChat,
                CoachAssignmentSupportType.AsyncChat,
              ].join(','),
            },
            {
              field: CoachAssignmentDynamicFilterField.Last,
              operation: DynamicFilterItemOperation.Eq,
              value: 'true',
            },
          ],
          operator: DynamicFilterOperator.And,
        },
      },
      caching: false,
      dataPath: 'coachAssignments',
      reducers: [{ name: 'updatePriorityOrderedCoachAssignment' }],
    }),
    applyFreeAppointment: Model.buildEffect({
      name: `${this.namespace}/applyFreeAppointment`,
      query: APPLY_FREE_APPOINTMENT,
      caching: false,
      dataPath: 'applyFreeEhrAppointment',
      reducers: [{ name: 'fetchUpcomingEhrAppointments' }],
    }),
    claimFreeAppointment: Model.buildEffect({
      name: `${this.namespace}/claimFreeAppointment`,
      query: CLAIM_FREE_APPOINTMENT,
      caching: false,
      dataPath: 'claimFreeEhrAppointment',
      reducers: [{ name: 'updateLastUserEhrAppointment' }],
    }),
    fetchMemberNotes: Model.buildEffect({
      name: `${this.namespace}/fetchMemberNotes`,
      query: ALL_NOTES,
      caching: false,
      dataPath: 'allNotes',
      reducers: [
        { name: 'updatedMemberNotes' },
        {
          name: 'updateMemberNotesUpdatedAtTimestamp',
          *payload(
            response: NoteCollection,
            { select }: any,
          ): Generator<NoteCollection, string, string> {
            const nextUpdatedAtTimestamp = orderBy(response.notes, 'updatedAt', 'desc')[0]
              ?.updatedAt
            const lastUpdatedAtTimestamp = yield select(memberNotesUpdatedAtTimestampSelector)

            if (!lastUpdatedAtTimestamp) {
              return nextUpdatedAtTimestamp || moment().toISOString()
            }

            const hasChanged =
              nextUpdatedAtTimestamp &&
              moment(nextUpdatedAtTimestamp).isAfter(moment(lastUpdatedAtTimestamp))

            if (hasChanged) {
              Storage.set(Storage.NUTRITIONIST_HUB_RED_DOT, true)
              return nextUpdatedAtTimestamp
            }

            return lastUpdatedAtTimestamp
          },
        },
      ],
    }),
    fetchEhrAppointment: Model.buildEffect({
      name: `${this.namespace}/fetchEhrAppointment`,
      query: EHR_APPOINTMENT,
      caching: false,
      dataPath: 'ehrAppointment',
    }),
    fetchUpcomingEhrAppointments: Model.buildEffect({
      name: `${this.namespace}/fetchUpcomingEhrAppointments`,
      query: EHR_APPOINTMENTS,
      caching: false,
      dataPath: 'ehrAppointments',
      variables: {
        filterKind: FilterAppointmentsKind.Upcoming,
        category: EhrAppointmentCategory.Coaching,
        sorts: [
          {
            direction: SortDirection.Asc,
            field: AppointmentEhrSortField.MeetingAt,
          },
        ],
      },
      reducers: [{ name: 'updateUpcomingEhrAppointments' }],
    }),
    fetchPastEhrAppointments: Model.buildEffect({
      name: `${this.namespace}/fetchEhrPastAppointments`,
      query: EHR_APPOINTMENTS,
      caching: false,
      dataPath: 'ehrAppointments',
      variables: {
        filterKind: FilterAppointmentsKind.Past,
        category: EhrAppointmentCategory.Coaching,
        sorts: [
          {
            direction: SortDirection.Desc,
            field: AppointmentEhrSortField.MeetingAt,
          },
        ],
      },
      reducers: [{ name: 'updatePastEhrAppointments' }],
    }),
    fetchAllEhrAppointments: Model.buildEffect({
      name: `${this.namespace}/fetchAllEhrAppointments`,
      query: EHR_APPOINTMENTS,
      caching: false,
      dataPath: 'ehrAppointments',
      variables: {
        filterKind: FilterAppointmentsKind.All,
        category: EhrAppointmentCategory.Coaching,
        sorts: [
          {
            direction: SortDirection.Desc,
            field: AppointmentEhrSortField.MeetingAt,
          },
        ],
      },
      reducers: [{ name: 'updateAllEhrAppointments' }],
    }),
    fetchPendingEhrAppointments: Model.buildEffect({
      name: `${this.namespace}/fetchPendingEhrAppointments`,
      query: EHR_APPOINTMENTS,
      caching: false,
      dataPath: 'ehrAppointments',
      variables: {
        filterKind: FilterAppointmentsKind.PendingSchedule,
        category: EhrAppointmentCategory.Coaching,
      },
      reducers: [{ name: 'updatePendingEhrAppointments' }],
    }),
    fetchAppointmentRecurrence: Model.buildEffect({
      name: `${this.namespace}/fetchAppointmentRecurrence`,
      query: FETCH_APPOINTMENT_RECURRENCE,
      caching: false,
      warnings: false, // disable warnings as response is empty
      dataPath: 'ehrAppointmentRecurrence',
      reducers: [{ name: 'updateAppointmentRecurrence' }],
    }),
    upsertAppointmentRecurrence: Model.buildEffect({
      name: `${this.namespace}/upsertAppointmentRecurrence`,
      query: UPSERT_APPOINTMENT_RECURRENCE,
      caching: false,
      dataPath: 'upsertEhrAppointmentRecurrence',
      reducers: [{ name: 'updateAppointmentRecurrence' }],
    }),
    deleteAppointmentRecurrence: Model.buildEffect({
      name: `${this.namespace}/deleteAppointmentRecurrence`,
      query: DELETE_APPOINTMENT_RECURRENCE,
      caching: false,
      dataPath: 'deleteEhrAppointmentRecurrence',
      reducers: [{ name: 'updateAppointmentRecurrence', payload: null }],
    }),
    fetchNextRecurringAppointmentSlot: Model.buildEffect({
      name: `${this.namespace}/fetchNextRecurringAppointmentSlot`,
      query: NEXT_RECURRING_APPOINTMENT_SLOT,
      caching: false,
      dataPath: 'nextRecurringAppointmentSlot',
      reducers: [{ name: 'updateNextRecurringAppointmentSlot' }],
    }),
    fetchCoachingCallAllowance: Model.buildEffect({
      name: `${this.namespace}/fetchCoachingCallAllowance`,
      query: BILLING_ALLOWANCES,
      caching: false,
      warnings: false,
      dataPath: 'billingAllowances.allowances',
      variables: {
        kinds: [BillingAllowanceKind.CoachingCall],
      },
      reducers: [
        {
          name: 'updateCoachingCallAllowance',
          payload: (allowances: BillingAllowance[]) => allowances[0] ?? null,
        },
      ],
    }),
    createChartNote: Model.buildEffect({
      name: `${this.namespace}/createChartNote`,
      query: CREATE_CHART_NOTE,
      caching: false,
      dataPath: 'createEhrChartNote',
    }),
    fetchInsuranceCardSubmission: Model.buildEffect({
      name: `${this.namespace}/fetchInsuranceCardSubmission`,
      query: FETCH_INSURANCE_CARD_SUBMISSION,
      caching: false,
      warnings: false, // disable warnings as response is empty
      dataPath: 'insuranceCardSubmission',
      reducers: [{ name: 'updateInsuranceCardSubmission' }],
    }),
    submitInsuranceCard: Model.buildEffect({
      name: `${this.namespace}/submitInsuranceCard`,
      query: SUBMIT_INSURANCE_CARD,
      caching: false,
      dataPath: 'submitInsuranceCard',
    }),
    rateCoach: Model.buildEffect({
      name: `${this.namespace}/rateCoachAppointment`,
      query: RATE_COACH_APPOINTMENT,
    }),
    fetchEhrAvailableDatesWithSlots: Model.buildEffect({
      name: `${this.namespace}/fetchEhrAvailableDatesWithSlots`,
      query: FETCH_EHR_AVAILABLE_DATES_WITH_SLOTS,
    }),
    sendVideoCallReferral: Model.buildEffect({
      name: `${this.namespace}/sendVideoCallReferral`,
      query: SEND_VIDEO_CALL_REFERRAL,
      dataPath: 'sendVideoCallReferral',
      isSuccess: (response: SendVideoCallReferralMutation['sendVideoCallReferral']) =>
        response.success,
    }),
    fetchAllCoachProfiles: Model.buildEffect({
      name: `${this.namespace}/fetchAllCoachProfiles`,
      query: FETCH_ALL_COACH_PROFILES,
      caching: false,
      dataPath: 'allCoachProfiles',
      reducers: [{ name: 'updateAllCoachProfiles' }],
    }),
    fetchCoachProfile: Model.buildEffect({
      name: `${this.namespace}/fetchCoachProfile`,
      query: FETCH_COACH_PROFILE,
      caching: false,
      dataPath: 'coachProfile',
      reducers: [{ name: 'updateCoachProfile' }],
    }),
    fetchCoachSwitchingSummary: Model.buildEffect({
      name: `${this.namespace}/fetchCoachSwitchingSummary`,
      query: FETCH_COACH_SWITCHING_SUMMARY,
      caching: false,
      dataPath: 'coachSwitchingSummary',
    }),
  }

  reducers = reducers
}
